import { FC, useMemo } from "react";
import { GetProgramFeeResponse } from "../../../../api/services/common/models";
import { PageContentApiP1Response } from "../../../../api/services/pageContent/models";
import { ITabs, MuiTabPanel, MuiTabs } from "../../../../components";
import {
  SOLUTION_OFFERING_CMC_SITECORE_KEY,
  TM_INDIVIDUAL_ASSESSMENT,
  TM_LEADER_COACHING,
} from "../../../../helpers/constants";
import { getValueOrBlank } from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { DocumentsTab } from "../../../createProgram/components/createProgramTabs/tabs/documents/DocumentsTab";
import { FeesAndExpenseMain } from "../../../createProgram/components/createProgramTabs/tabs/feesAndExpense/FeesAndExpenseMain";
import {
  getTabIds,
  getTabIndexes,
  getTabPanels,
} from "../../../createProgram/helper";
import {
  ProgramDetail,
  ParticipantDetail,
  ProgramStatusList,
} from "../../../createProgram/modelsAndSchemas/Models";
import {
  feesAndExpensesSiteCoreKeys,
  programDocumentsTabSiteCoreKeys,
  programSiteCoreKeys,
} from "../../../createProgram/siteCore/SiteCoreKeys";
import { ProgramAndParticipantWrapper } from "./ProgramAndParticipant.style";
import { ParticipantTab } from "./tabs/participant/ParticipantTab";
import { ProgramTab } from "./tabs/participant/ProgramTab";
import { ViewProgramFeeTab } from "./tabs/participant/ViewProgramFeeTab";

// API divided in to 2 parts as BE team does not have time - Later this will be moved to 1 API
// Search for this exact string to find the other part of the API
interface ProgramAndParticipantProps {
  pageId: string;
  participantDetail?: ParticipantDetail;
  programDetail?: ProgramDetail;
  programStatusList?: ProgramStatusList[];
  selectedTab: number;
  setSelectedTab: (tabIndex: number) => void;
  refetchProgramStatusList: () => void;
  programFeeDetailsData: GetProgramFeeResponse;
  programId?: string;
  pageContentData?: PageContentApiP1Response;
}

export const ProgramAndParticipant: FC<ProgramAndParticipantProps> = ({
  pageId,
  participantDetail,
  programDetail,
  selectedTab,
  setSelectedTab,
  programStatusList,
  refetchProgramStatusList,
  programFeeDetailsData,
  programId,
  pageContentData,
}) => {
  const { fetchLabel, fetchComponentTitle } = useSiteCore();

  const getLabel = (parentKey: string, key: string) => {
    return fetchLabel(parentKey, key);
  };

  const TAB_INDEX = getTabIndexes(true);
  const TAB_IDS = getTabIds(pageId, true);
  const TAB_PANELS = getTabPanels(pageId, true);

  const tabIdProgram = TAB_IDS.PROGRAM;
  const tabIdParticipant = TAB_IDS.PARTICIPANT;
  const tabIdDocuments = TAB_IDS.DOCUMENTS;
  const tabIdProgramFee = TAB_IDS.PROGRAM_FEE;
  const tabIdFeesAndExpenses = TAB_IDS.FEES_AND_EXPENSES;

  const tabs: ITabs[] = [
    {
      id: tabIdProgram,
      label: getLabel(
        programSiteCoreKeys.keyProgramDetails,
        programSiteCoreKeys.program,
      ),
    },
    {
      id: tabIdParticipant,
      label: getLabel(
        programSiteCoreKeys.keyProgramDetails,
        programSiteCoreKeys.participant,
      ),
    },
    {
      id: tabIdDocuments,
      label: fetchComponentTitle(
        programDocumentsTabSiteCoreKeys.documentsTitle,
      ),
    },
    {
      id: tabIdProgramFee,
      label: fetchComponentTitle(programSiteCoreKeys.programFeeTitle),
    },
    {
      id: tabIdFeesAndExpenses,
      label: fetchComponentTitle(feesAndExpensesSiteCoreKeys.feesAndExpenses),
    },
  ];

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
  };

  const isCMCProgram = useMemo(() => {
    return (
      programDetail?.solutionOfferingSiteCoreKey ===
      SOLUTION_OFFERING_CMC_SITECORE_KEY
    );
  }, [programDetail]);

  const isTMAssessmentProgram = useMemo(() => {
    return programDetail?.tmProgramType === TM_INDIVIDUAL_ASSESSMENT;
  }, [programDetail?.tmProgramType]);

  const isTMLeaderCoachingProgram = useMemo(() => {
    return programDetail?.tmProgramType === TM_LEADER_COACHING;
  }, [programDetail?.tmProgramType]);

  return (
    <ProgramAndParticipantWrapper>
      <MuiTabs
        tabs={tabs}
        value={selectedTab}
        onChange={onTabChange}
        tabsTestIds={[
          tabIdProgram,
          tabIdParticipant,
          tabIdProgramFee,
          tabIdFeesAndExpenses,
          tabIdDocuments,
        ]}
        className="program-participant-tab"
      >
        <MuiTabPanel
          panelId={TAB_PANELS.PROGRAM}
          value={selectedTab}
          index={TAB_INDEX.PROGRAM}
          isPanelWithMount={true}
          data-testid={`${pageId}-tab-panel-1`}
          ariaLabelledById={tabIdProgram}
        >
          <ProgramTab
            pageId={pageId}
            programDetail={programDetail}
            programStatusList={programStatusList}
            refetchProgramStatusList={refetchProgramStatusList}
            isCMCProgram={isCMCProgram}
            isTMAssessmentProgram={isTMAssessmentProgram}
            isTMLeaderCoachingProgram={isTMLeaderCoachingProgram}
          />
        </MuiTabPanel>
        <MuiTabPanel
          panelId={TAB_PANELS.PARTICIPANT}
          value={selectedTab}
          index={TAB_INDEX.PARTICIPANT}
          isPanelWithMount={true}
          data-testid={`${pageId}-tab-panel-2`}
          ariaLabelledById={tabIdParticipant}
        >
          <ParticipantTab
            pageId={pageId}
            programId={getValueOrBlank(programId)}
            participantDetail={participantDetail}
            pageContentData={pageContentData}
            nextPersona={programDetail?.nextPersona}
            isCMCProgram={isCMCProgram}
            isTMAssessmentProgram={isTMAssessmentProgram}
            isTMLeaderCoachingProgram={isTMLeaderCoachingProgram}
            deliveryCountry={getValueOrBlank(programDetail?.deliveryCountry)}
          />
        </MuiTabPanel>
        <MuiTabPanel
          panelId={TAB_PANELS.DOCUMENTS}
          value={selectedTab}
          index={TAB_INDEX.DOCUMENTS}
          isPanelWithMount
          ariaLabelledById={tabIdDocuments}
        >
          <DocumentsTab
            isViewOnly
            pageId={pageId}
            programId={getValueOrBlank(programId)}
          />
        </MuiTabPanel>
        <MuiTabPanel
          panelId={TAB_PANELS.PROGRAM_FEE}
          value={selectedTab}
          index={TAB_INDEX.PROGRAM_FEE}
          isPanelWithMount={true}
          data-testid={`${pageId}-tab-panel-3`}
          ariaLabelledById={tabIdProgramFee}
        >
          <ViewProgramFeeTab
            programFeeDetailsData={programFeeDetailsData}
            programData={programDetail}
            pageId={pageId}
          />
        </MuiTabPanel>
        <MuiTabPanel
          panelId={TAB_PANELS.FEES_AND_EXPENSES}
          value={selectedTab}
          index={TAB_INDEX.FEES_AND_EXPENSES}
          isPanelWithMount={true}
          data-testid={`${pageId}-tab-panel-4`}
          ariaLabelledById={tabIdFeesAndExpenses}
        >
          <FeesAndExpenseMain
            programDetail={programDetail}
            programId={programId ?? ""}
            isViewOnly={true}
            isRefetch={selectedTab === TAB_INDEX.FEES_AND_EXPENSES}
          />
        </MuiTabPanel>
      </MuiTabs>
    </ProgramAndParticipantWrapper>
  );
};
